import React, { useState, useEffect } from 'react';
import { User, FileSignature, Clock, Check, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { signatureService } from '../services/signatureService';

const SignatureRequestsPanel = ({ documentId, onRequestSelect }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSignatureRequests = async () => {
      try {
        setLoading(true);
        
        // Get document signatures to understand the current state
        const data = await signatureService.getDocumentSignatures(documentId);
        
        // Use the requests from the API response
        if (data?.requests) {
          setRequests(data.requests);
        } else {
          setRequests([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching signature requests:', err);
        setError('Failed to load signature requests');
        setLoading(false);
      }
    };

    if (documentId) {
      fetchSignatureRequests();
    }
  }, [documentId]);

  const getStatusIcon = (status, isCurrentUser) => {
    switch (status) {
      case 'completed':
        return <Check size={16} className="text-green-600" />;
      case 'declined':
        return <X size={16} className="text-red-600" />;
      case 'pending':
        return isCurrentUser 
          ? <Clock size={16} className="text-blue-600" />
          : <Clock size={16} className="text-amber-600" />;
      default:
        return <AlertTriangle size={16} className="text-gray-600" />;
    }
  };

  const getStatusText = (status, isCurrentUser) => {
    switch (status) {
      case 'completed':
        return 'Signed';
      case 'declined':
        return 'Declined';
      case 'pending':
        return isCurrentUser ? 'Your turn to sign' : 'Waiting for signature';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status, isCurrentUser) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 text-green-700';
      case 'declined':
        return 'bg-red-50 text-red-700';
      case 'pending':
        return isCurrentUser 
          ? 'bg-blue-50 text-blue-700'
          : 'bg-amber-50 text-amber-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const handleRequestClick = (request) => {
    const isCurrentUser = request.userId === user?.id;
    if (isCurrentUser && request.status === 'pending') {
      onRequestSelect && onRequestSelect(request);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading signature requests...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center text-red-600">
          <AlertTriangle size={20} className="mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <FileSignature size={20} className="mr-2 text-blue-600" />
          Signature Requests
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Track who needs to sign this document
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {requests.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <FileSignature size={48} className="mx-auto text-gray-400 mb-4" />
            <h4 className="text-gray-900 font-medium mb-2">No signature requests</h4>
            <p className="text-gray-500 text-sm">
              This document doesn't have any signature requests yet.
            </p>
          </div>
        ) : (
          requests.map((request) => {
            const isCurrentUser = request.userId === user?.id;
            return (
              <div
                key={request.requestId}
                className={`px-6 py-4 transition-colors ${
                  isCurrentUser && request.status === 'pending'
                    ? 'hover:bg-blue-50 cursor-pointer'
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => handleRequestClick(request)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User size={20} className="text-gray-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {request.userName}
                          {isCurrentUser && (
                            <span className="text-blue-600 font-normal"> (You)</span>
                          )}
                        </p>
                        <span className="text-xs text-gray-500">
                          #{request.order}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {request.userEmail}
                      </p>
                      {request.signatureFields && (
                        <p className="text-xs text-gray-400 mt-1">
                          {request.signatureFields.length} signature field{request.signatureFields.length !== 1 ? 's' : ''} required
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {getStatusIcon(request.status, isCurrentUser)}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      getStatusColor(request.status, isCurrentUser)
                    }`}>
                      {getStatusText(request.status, isCurrentUser)}
                    </span>
                  </div>
                </div>

                {isCurrentUser && request.status === 'pending' && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800 flex items-center">
                      <FileSignature size={16} className="mr-2" />
                      Click to start signing this document
                    </p>
                  </div>
                )}

                {!isCurrentUser && request.status === 'pending' && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-md">
                    <p className="text-sm text-amber-800 flex items-center">
                      <Clock size={16} className="mr-2" />
                      Waiting for {request.userName} to sign
                    </p>
                  </div>
                )}

                {/* Render signature fields for this request */}
                {request.signatureFields && request.signatureFields.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-2">Signature Fields:</div>
                    <div className="grid grid-cols-2 gap-2">
                      {request.signatureFields.map((field, index) => (
                        <div
                          key={field.id || index}
                          className="p-2 bg-gray-50 rounded border border-gray-200"
                        >
                          <div className="text-xs font-medium text-gray-700">
                            Field {index + 1}
                          </div>
                          <div className="text-xs text-gray-500">
                            Page {field.page || 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SignatureRequestsPanel; 